import { signOutStaffSession } from 'api-calls/sign-in';
import { refreshGoogleToken, tokenNeedsRefresh } from 'api-calls/refresh';
import { createContext, useContext, useEffect, useState } from 'react';
import {
  deleteToken,
  getAccessToken,
  getUser,
  hasStaffSession,
} from 'utils/auth';

// How often to check whether the Google ID token needs refreshing.
const CHECK_INTERVAL = 60 * 1000;

interface AuthContextValue {
  user: any;
  token: any;
  isTokenExpired: boolean;
  isLoading: boolean;
  signOut: () => void;
  isAuthenticated: boolean;
  isError: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Staff stay signed in for the staff session (StaffSessionTTL on the server,
// from the second factor at sign-in).
// The Google ID token it pairs with lasts an hour, so it is refreshed on the
// server (POST /auth/refresh-token) a few minutes before it expires.
export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState(() => getUser());
  const [accessToken, setAccessTokenState] = useState(() => getAccessToken());
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const refresh = async () => {
    const ok = await refreshGoogleToken();
    setIsError(!ok);
    // Reload state from storage: the refresh stored a new token, or cleared
    // the session.
    setUser(getUser());
    setAccessTokenState(getAccessToken());
    return ok;
  };

  // On load, refresh first if the token has expired or is about to.
  useEffect(() => {
    const verify = async () => {
      if (getAccessToken()?.token && hasStaffSession() && tokenNeedsRefresh()) {
        await refresh();
      }
      setIsLoading(false);
    };
    verify();
  }, []);

  // Keep the token fresh while signed in. Also check when the tab comes back
  // into view, since timers are paused in sleeping or background tabs.
  useEffect(() => {
    if (!user?.email) return;

    const check = () => {
      if (!hasStaffSession() || !getAccessToken()?.token) return;
      if (tokenNeedsRefresh()) {
        refresh().then(ok => {
          if (!ok && !getUser()) {
            window.location.assign(
              `/login?url=${encodeURIComponent(window.location.pathname)}`
            );
          }
        });
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };

    check();
    const id = setInterval(check, CHECK_INTERVAL);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user?.email]);

  const signOut = async () => {
    await signOutStaffSession();
    deleteToken();
    setUser(null);
    setAccessTokenState(null);
    window.location.assign('/login');
  };

  const value: AuthContextValue = {
    user,
    token: accessToken,
    isTokenExpired: !!accessToken?.token && tokenNeedsRefresh(),
    isLoading,
    signOut,
    isAuthenticated: !!(user && user.email),
    isError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
